/** @file Commands of the Weights panel: binding conversions, rebind, smoothing, per-vertex influence edits. */
import type { CommandCall, Doc, Id } from '@pose2d/format/types';
import type { MeshTarget } from '../../tools/editMesh/meshTarget';
import { staleBind } from '../../tools/editMesh/staleBind';
import { paintStroke } from '../../tools/paintWeights/paintStroke';
import { setShare } from '../../tools/paintWeights/setShare';
import { weightRows } from '../../tools/paintWeights/weightRows';

/** One call per panel button; the setters return null when there is nothing to change. */
export const weightsCalls = {
  /** rigid ↔ skinned on the edited slot's bone (a new skinned mesh starts fully on it, §6.7). */
  binding(doc: Doc, t: MeshTarget, binding: 'rigid' | 'skinned'): CommandCall {
    return { name: 'mesh.setBinding', input: { target: t.attachment, binding, bone: paintStroke.bone(doc, t, null) } };
  },
  /** Re-captures the bind at the current setup pose. */
  rebind(t: MeshTarget): CommandCall {
    return staleBind.rebind(t.attachment);
  },
  /** One smoothing pass over the selected vertices (all when none is selected). */
  smooth(t: MeshTarget, selected: readonly number[]): CommandCall {
    return { name: 'mesh.smoothWeights', input: { target: t.attachment, ...(selected.length === 0 ? {} : { vertices: [...selected] }) } };
  },
  /**
   * `bone` at share `weight` on every selected vertex (0 removes it), through setShare: one vertex →
   * mesh.setVertexWeights, several → mesh.setWeights; null for a rigid mesh or an empty selection.
   */
  share(t: MeshTarget, selected: readonly number[], bone: Id, weight: number, autoNormalize: boolean): CommandCall | null {
    const rows = weightRows.of(t.mesh);
    const [only, ...more] = selected;
    if (rows === null || only === undefined) return null;
    if (more.length === 0) {
      return { name: 'mesh.setVertexWeights', input: { target: t.attachment, vertex: only, weights: setShare(rows[only] ?? [], bone, weight, autoNormalize) } };
    }
    for (const v of selected) rows[v] = setShare(rows[v] ?? [], bone, weight, autoNormalize);
    return weightRows.call(t.attachment, rows);
  },
  /**
   * The batch a weight edit (share, smooth) commits, one undo step: in Setup mode a stale bind is re-captured first
   * (mesh.rebindMesh), so no vertex moves at the setup pose (T-473); Animate mode sends `call` alone; [] for null.
   */
  edit(doc: Doc, t: MeshTarget, call: CommandCall | null, animate: boolean): CommandCall[] {
    return call === null ? [] : staleBind.batch(doc, t, [call], animate ? 'animate' : 'setup');
  },
};
